import axios from "axios";
import * as Sentry from "@sentry/react";
import toast from "react-simple-toasts";
import { baseURL } from "./constant";
import { clearInvestingLocalState, clearOnboardingData } from "./onboardingRoutes";
import { authAttemptId, authStep, watchAuthStep } from "./authDiagnostics";

const recordAuthResponse = (response, error) => {
  const pending = (response?.config || error?.config)?.loginDiagnostic;
  if (!pending) return;
  pending.stop();
  let server;
  try { server = JSON.parse(response?.headers?.["x-pm-auth-diagnostic"] || "null"); } catch { /* Older/proxy responses may omit diagnostics. */ }
  authStep(error ? "request_failed" : "request_succeeded", {
    endpoint: pending.endpoint, status: response?.status, ms_request: Date.now() - pending.started,
    error_code: error?.code, server,
    mfa_state: response?.data?.mfa_state, mfa_required: response?.data?.mfa_required,
  }, error ? "warning" : undefined);
};

// Track if we're already handling a session expiry to prevent multiple redirects
let isHandlingSessionExpiry = false;

/**
 * Clear ALL client-side session state (auth tokens, onboarding cache, investing
 * cache, Sentry user). Single source of truth so logout / session-end
 * paths (LayoutInvesting, LayoutOnboarding, LogoutModal,
 * SpouseConfirm, handleSessionExpired) can't drift. Does NOT navigate — callers
 * own redirect behavior.
 */
export const clearUserSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("registrationToken");
  localStorage.removeItem("signupEmail");
  localStorage.removeItem("isSpouseSession");
  clearOnboardingData();
  clearInvestingLocalState();
  Sentry.setUser(null);
};

/**
 * Handle session expiry (401 response) - clears user data, shows toast, and redirects to login
 * @param {string} context - Optional context for Sentry logging (e.g., "fetching dashboard")
 */
export const handleSessionExpired = (context = "API request") => {
  // Prevent multiple simultaneous session expiry handlers
  if (isHandlingSessionExpiry) {
    return;
  }
  isHandlingSessionExpiry = true;

  // Log to Sentry with context
  Sentry.withScope((scope) => {
    scope.setTag("session_expired", true);
    scope.setExtra("context", context);
    scope.setExtra("user_notified", true);
    Sentry.captureMessage("Session expired - user signed out", "info");
  });

  // Show toast notification to user
  toast("Your session has expired. Please sign in again.", { theme: "failure" });

  // Capture product intent BEFORE clearing so login can resume product routes.
  const wasOnInvesting = typeof window !== "undefined" && window.location.pathname.startsWith("/investing");

  // Clear all user data (tokens + onboarding + investing cache + Sentry).
  clearUserSession();

  if (wasOnInvesting) {
    localStorage.setItem("postLoginRedirect", "/investing");
  }

  // Redirect to login after a brief delay so user can see the toast
  setTimeout(() => {
    window.location.href = "/login";
  }, 1500);
};

export const getAxios = (isFormData = false) => {
  let axiosInstance;

  const token = window.localStorage.getItem("token");

  const headers = {
    Accept: "application/json",
    ...(token && { Authorization: "Token " + token }),
    ...(isFormData
      ? {} // ⚠️ Let Axios handle Content-Type when using FormData
      : { "Content-Type": "application/json" }),
  };

  axiosInstance = axios.create({
    headers,
    timeout: 3000000,
  });

  axiosInstance.interceptors.request.use((config) => {
    if (config.url?.includes("/auth/")) {
      const id = authAttemptId();
      if (id) {
        config.headers["X-PM-Auth-Attempt"] = id;
        config.loginDiagnostic = { endpoint: config.url, started: Date.now(),
          stop: watchAuthStep("request", { endpoint: config.url, method: config.method }) };
      }
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    function (response) {
      recordAuthResponse(response);
      return Promise.resolve(response);
    },
    function (error) {
      recordAuthResponse(error.response, error);
      // Server errors (5xx): never surface raw backend messages to users.
      // Rewrite the error body to a friendly maintenance message (every
      // existing toast reads error.response.data.error) and keep the real
      // error in the console.
      if (error.response?.status >= 500) {
        console.log(
          "Backend error (shown to user as maintenance message):",
          error.response.status,
          error.config?.url,
          error.response.data
        );
        const maintenanceMessage =
          "We're doing some routine maintenance right now - we should be back up within 30 minutes. Need anything in the meantime? Email help@getprimemeridian.com";
        if (error.response.data && typeof error.response.data === "object") {
          error.response.data.error = maintenanceMessage;
        } else {
          error.response.data = { error: maintenanceMessage };
        }
      }
      // Handle 401 Unauthorized - session expired
      if (error.response?.status === 401) {
        const requestUrl = error.config?.url || "unknown endpoint";
        handleSessionExpired(`401 from ${requestUrl}`);
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export const post = (uri, data, isFormData = false) => {
  return new Promise((resolve, reject) => {
    getAxios(isFormData)
      .post(baseURL + uri, data)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const get = (uri) => {
  return new Promise((resolve, reject) => {
    getAxios()
      .get(baseURL + uri)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const del = (uri) => {
  return new Promise((resolve, reject) => {
    getAxios()
      .delete(baseURL + uri)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};
